import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../App';
import { TrendingUp, TrendingDown, Users, Wallet, Shield, CreditCard, RefreshCw, Settings, Edit2, Trash2, History, RotateCcw, Filter, ArrowUpDown, Check, X, ShieldOff, Building2, AlertTriangle, IndianRupee, Award, Target, Zap, ArrowRight, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import AddTransactionDialog from '../components/AddTransactionDialog';
import DataSourceHealthBadge from '../components/DataSourceHealthBadge';
import RefreshHistoryModal from '../components/RefreshHistoryModal';
import ZeroStateDashboard from './ZeroStateDashboard';

const StatCard = ({ icon: Icon, label, value, subtext, trend, testId, onClick }) => (
  <div className="card cursor-pointer hover:shadow-xl transition-all" data-testid={testId} onClick={onClick}>
    <div className="flex items-start justify-between mb-3 sm:mb-4">
      <div className="p-2 sm:p-3 bg-[#D1FAE5] rounded-lg"><Icon className="text-primary" size={20} /></div>
      {trend && (
        <div className={`flex items-center gap-1 text-xs sm:text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
    <div>
      <p className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1">₹{value?.toLocaleString('en-IN') || 0}</p>
      {subtext && <p className="text-xs sm:text-sm text-gray-600">{subtext}</p>}
    </div>
  </div>
);

// Demo Mode Toggle Component - for sales demonstrations
const DemoModeToggle = ({ isDemoPremium, demoModeShowZeroState, setDemoModeShowZeroState }) => (
  <div className="fixed bottom-6 right-6 z-50">
    <div className="bg-white rounded-2xl shadow-2xl border-2 border-primary/20 p-4 max-w-xs">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-gradient-to-br from-primary to-emerald-600 rounded-lg">
          <Zap className="text-white" size={20} />
        </div>
        <div>
          <p className="font-bold text-gray-900">Demo Mode</p>
          <p className="text-xs text-gray-500">{isDemoPremium ? 'Premium' : 'Free'} Plan Demo</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
        <span className="text-sm font-medium text-gray-700">
          {demoModeShowZeroState ? '📭 Zero State' : '📊 With Data'}
        </span>
        <button
          onClick={() => setDemoModeShowZeroState(!demoModeShowZeroState)}
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none ${
            demoModeShowZeroState ? 'bg-gray-300' : 'bg-primary'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
              demoModeShowZeroState ? 'translate-x-1' : 'translate-x-8'
            }`}
          />
        </button>
      </div>
      
      <p className="text-xs text-gray-400 mt-2 text-center">
        Toggle to switch between empty &amp; populated views
      </p>
    </div>
  </div>
);

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [perfPeriod, setPerfPeriod] = useState('overall'); // overall | yearly | monthly
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal states
  const [showHoldingsModal, setShowHoldingsModal] = useState(false);
  const [showRefreshHistory, setShowRefreshHistory] = useState(false);
  const [showCompaniesModal, setShowCompaniesModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showLASModal, setShowLASModal] = useState(false);
  const [showMTFModal, setShowMTFModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ type: '', data: null, message: '' });
  const [showPledgeDialog, setShowPledgeDialog] = useState(false);
  const [pledgeData, setPledgeData] = useState({ holdingId: null, symbol: '', quantity: 0, lasAccountId: '', pledgeQuantity: 0 });
  const [showUnpledgeDialog, setShowUnpledgeDialog] = useState(false);
  const [unpledgeData, setUnpledgeData] = useState({ holdingId: null, symbol: '', quantity: 0, unpledgeQuantity: 0 });
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [portfolioPerformance, setPortfolioPerformance] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [latestChange, setLatestChange] = useState(null);
  const [portfolioSwings, setPortfolioSwings] = useState(null);
  const [dailySnapshot, setDailySnapshot] = useState(null);
  
  // Demo Mode state - for sales demonstrations
  const [demoModeShowZeroState, setDemoModeShowZeroState] = useState(true);
  const isDemoAccount = user?.email === 'demo-free@maadabbu.com' || user?.email === 'demo-premium@maadabbu.com';
  const isDemoPremium = user?.email === 'demo-premium@maadabbu.com';
  
  // Data states
  const [holdings, setHoldings] = useState([]);
  const [members, setMembers] = useState([]);
  const [lasAccounts, setLASAccounts] = useState([]);
  const [mtfAccounts, setMTFAccounts] = useState([]);
  const [changeHistory, setChangeHistory] = useState([]);
  
  // Filter & Edit states for Holdings Modal
  const [holdingFilter, setHoldingFilter] = useState('all');
  const [selectedMember, setSelectedMember] = useState('all');
  const [selectedQualityTag, setSelectedQualityTag] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'symbol', direction: 'asc' });
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Company view states
  const [companySortConfig, setCompanySortConfig] = useState({ key: 'symbol', direction: 'asc' });
  const [companyMemberFilter, setCompanyMemberFilter] = useState('all');
  
  // Dismissed alerts state
  const [dismissedAlerts, setDismissedAlerts] = useState({});

  const qualityTags = ['New', 'Moderate', 'Low', 'High', 'V Low', 'VV Low', 'Unknown'];

  useEffect(() => {
    fetchSummary();
    fetchLatestChange();
  }, []);

  // Refetch only the performance summary when the user toggles the period pill.
  useEffect(() => {
    let cancelled = false;
    api.get(`/performance/summary?period=${perfPeriod}`)
      .then(res => { if (!cancelled) setPerformanceSummary(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [perfPeriod]);

  const fetchSummary = async (retryCount = 0) => {
    try {
      const [dashRes, perfRes, swingsRes, dailyRes] = await Promise.all([
        api.get('/dashboard/summary'),
        api.get(`/performance/summary?period=${perfPeriod}`).catch(() => ({ data: null })),
        api.get('/dashboard/portfolio-swings').catch(() => ({ data: null })),
        api.get('/dashboard/daily-snapshot').catch(() => ({ data: null })),
      ]);
      setSummary(dashRes.data);
      setPerformanceSummary(perfRes.data);
      setPortfolioSwings(swingsRes.data);
      setDailySnapshot(dailyRes.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      // Retry up to 2 times with exponential backoff
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
        console.log(`Retrying dashboard fetch in ${delay}ms (attempt ${retryCount + 2}/3)`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchSummary(retryCount + 1);
      }
      toast.error('Failed to load dashboard. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestChange = async () => {
    try {
      const res = await api.get('/performance/latest-change');
      setLatestChange(res.data);
    } catch (err) {
      console.error('Error fetching latest change:', err);
    }
  };

  const fetchPortfolioPerformance = async (period = 'month') => {
    try {
      const res = await api.get(`/performance/history?period=${period}`);
      setPortfolioPerformance(res.data);
    } catch (err) {
      console.error('Error fetching portfolio performance:', err);
    }
  };

  const handlePortfolioClick = () => {
    fetchPortfolioPerformance(selectedPeriod);
    setShowPortfolioModal(true);
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    fetchPortfolioPerformance(period);
  };

  const handleRefreshPrices = async () => {
    if (refreshing) return; // Prevent spam-click
    setRefreshing(true);
    toast.info('Refreshing stock prices... This may take 30-60 seconds', { duration: 4000 });

    try {
      // Use longer timeout for price refresh (3 minutes — yfinance + bulk update)
      const res = await api.post('/prices/refresh', {}, { timeout: 180000 });
      const change = res.data.value_change || 0;
      const changePercent = res.data.change_percent || 0;
      const changeColor = change >= 0 ? 'text-green-600' : 'text-red-600';
      const changeSign = change >= 0 ? '+' : '';
      
      // Calculate totals for display
      const stocksRefreshed = res.data.stocks_refreshed || 0;
      const stocksAttempted = res.data.stocks_attempted || stocksRefreshed;
      const mfRefreshed = res.data.mf_refreshed || 0;
      const mfAttempted = res.data.mf_attempted || mfRefreshed;
      const totalRefreshed = stocksRefreshed + mfRefreshed;
      const totalAttempted = stocksAttempted + mfAttempted;
      const failed = totalAttempted - totalRefreshed;
      const unlisted = res.data.unlisted_skipped || 0;
      const failedTickers = res.data.failed_tickers || [];
      
      toast.success(
        <div>
          <div className="font-semibold">{totalRefreshed}/{totalAttempted} prices refreshed</div>
          <div className="text-xs text-gray-500">
            {stocksRefreshed}/{stocksAttempted} stocks + {mfRefreshed}/{mfAttempted} MFs
            {failed > 0 && <span className="text-orange-500"> • {failed} unavailable</span>}
            {unlisted > 0 && <span> • {unlisted} unlisted skipped</span>}
          </div>
          <div className={changeColor}>
            Portfolio: {changeSign}₹{Math.round(Math.abs(change)).toLocaleString('en-IN')} ({changeSign}{changePercent.toFixed(2)}%)
          </div>
        </div>
      );
      
      // Show failed tickers in a separate warning toast if any
      if (failedTickers.length > 0) {
        const markUnlisted = async (displayName, toastId) => {
          try {
            await api.post('/symbols/mark-unlisted', { display_name: displayName, reason: 'delisted' });
            toast.success(`${displayName} marked as unlisted — it won't be attempted in future refreshes`);
            toast.dismiss(toastId);
          } catch (e) {
            toast.error(e.response?.data?.detail || `Could not mark ${displayName} unlisted`);
          }
        };
        toast.warning(
          (t) => (
            <div>
              <div className="font-semibold text-orange-700 mb-1">Failed to refresh {failedTickers.length} stocks:</div>
              <div className="text-[11px] text-gray-500 mb-1">Yahoo has no live price — likely delisted/suspended. Mark them as unlisted so they stop appearing here.</div>
              <div className="text-xs mt-1 max-h-40 overflow-y-auto space-y-1">
                {failedTickers.slice(0, 10).map((tk, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 py-0.5 border-b last:border-b-0">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{tk.display_name}</div>
                      <div className="text-[10px] text-gray-400">tried: {tk.ticker}</div>
                    </div>
                    <button
                      onClick={() => markUnlisted(tk.display_name, t)}
                      className="text-[10px] px-2 py-1 bg-amber-100 text-amber-800 hover:bg-amber-200 rounded font-medium whitespace-nowrap"
                      data-testid={`mark-unlisted-${tk.display_name}`}
                    >
                      Mark unlisted
                    </button>
                  </div>
                ))}
                {failedTickers.length > 10 && (
                  <div className="text-gray-500 italic">...and {failedTickers.length - 10} more</div>
                )}
              </div>
            </div>
          ),
          { duration: 20000 }
        );
      }

      // Data source flakiness warning — separate toast so it's visible at a glance
      const health = res.data.data_source_health || 'healthy';
      if (health === 'slow') {
        toast.warning(
          <div>
            <div className="font-semibold text-amber-700">Yahoo Finance is being slow today</div>
            <div className="text-xs mt-1 text-gray-600">
              Only {stocksRefreshed}/{stocksAttempted} stocks refreshed successfully. Some prices may be a few minutes stale.
            </div>
          </div>,
          { duration: 7000 }
        );
      } else if (health === 'degraded') {
        toast.error(
          <div>
            <div className="font-semibold">Data source is degraded</div>
            <div className="text-xs mt-1">
              Yahoo Finance returned prices for only {stocksRefreshed}/{stocksAttempted} stocks. Try again in a few minutes.
            </div>
          </div>,
          { duration: 8000 }
        );
      }
      
      // Silent follow-up fetches — failures here must NOT trigger the main error toast
      // because the actual refresh already succeeded above.
      Promise.allSettled([
        fetchSummary(),
        fetchLatestPriceChange(),
        showHoldingsModal ? fetchHoldings() : Promise.resolve(),
        showCompaniesModal ? fetchHoldings() : Promise.resolve(),
      ]).catch(() => { /* ignored */ });
    } catch (err) {
      console.error('Price refresh error:', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        // Likely completed server-side; suggest user refresh page shortly
        toast.warning(
          <div>
            <div className="font-semibold">Refresh is taking longer than expected</div>
            <div className="text-xs mt-1">It may still be completing in the background. Reload the page in a minute to see updated prices.</div>
          </div>,
          { duration: 8000 }
        );
        // Still try to refresh summary in case backend did complete
        setTimeout(() => { fetchSummary(); fetchLatestPriceChange(); }, 5000);
      } else if (err.response?.status === 502 || err.response?.status === 504) {
        toast.warning('Gateway timed out, but refresh may still be running. Please wait a moment and reload.', { duration: 6000 });
      } else if (err.response?.status >= 500) {
        toast.error('Server error during price refresh. Please try again in a moment.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to refresh prices. Please try again.');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const [latestPriceChange, setLatestPriceChange] = useState(null);

  const fetchLatestPriceChange = async () => {
    try {
      const res = await api.get('/prices/latest-change');
      if (res.data.has_data) {
        setLatestPriceChange(res.data);
      }
    } catch (err) {
      console.error('Error fetching latest price change:', err);
    }
  };

  useEffect(() => {
    fetchLatestPriceChange();
  }, []);

  const fetchHoldings = async () => {
    try {
      const [holdingsRes, membersRes] = await Promise.all([
        api.get('/holdings'),
        api.get('/family-members')
      ]);
      setHoldings(holdingsRes.data);
      setMembers(membersRes.data);
    } catch (err) {
      toast.error('Failed to load holdings');
    }
  };

  const openHoldingsModal = async () => {
    await fetchHoldings();
    setShowHoldingsModal(true);
  };

  const openCompaniesModal = async () => {
    await fetchHoldings();
    setShowCompaniesModal(true);
  };

  const fetchMembers = async () => {
    try {
      const res = await api.get('/family-members');
      setMembers(res.data);
      setShowMembersModal(true);
    } catch (err) {
      toast.error('Failed to load members');
    }
  };

  const fetchLASAccounts = async () => {
    try {
      const [accountsRes, membersRes, holdingsRes] = await Promise.all([
        api.get('/las-accounts'),
        api.get('/family-members'),
        api.get('/holdings')
      ]);
      setLASAccounts(accountsRes.data);
      setMembers(membersRes.data);
      setHoldings(holdingsRes.data);
      setShowLASModal(true);
    } catch (err) {
      toast.error('Failed to load LAS accounts');
    }
  };

  const fetchMTFAccounts = async () => {
    try {
      const [accountsRes, membersRes] = await Promise.all([
        api.get('/mtf-accounts'),
        api.get('/family-members')
      ]);
      setMTFAccounts(accountsRes.data);
      setMembers(membersRes.data);
      setShowMTFModal(true);
    } catch (err) {
      toast.error('Failed to load MTF accounts');
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/change-history?entity_type=holding&limit=50');
      setChangeHistory(res.data);
      setShowHistoryModal(true);
    } catch (err) {
      toast.error('Failed to load history');
    }
  };

  // Confirmation dialog handlers
  const showConfirm = (type, data, message) => {
    setConfirmAction({ type, data, message });
    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    const { type, data } = confirmAction;
    setShowConfirmDialog(false);
    
    if (type === 'rollback') {
      try {
        const res = await api.post(`/change-history/${data}/rollback`);
        toast.success(res.data.message);
        fetchHistory();
        fetchHoldings();
        fetchSummary();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to rollback');
      }
    } else if (type === 'delete') {
      try {
        await api.delete(`/holdings/${data}`);
        toast.success('Holding deleted');
        fetchHoldings();
        fetchSummary();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to delete holding');
      }
    }
  };

  const handleRollback = (historyId, description) => {
    showConfirm('rollback', historyId, `Are you sure you want to rollback this change?\n\n${description}`);
  };

  // Holdings Edit Functions
  const handleEdit = (holding) => {
    setEditingId(holding.id);
    setEditFormData({
      symbol: holding.symbol,
      quantity: holding.quantity,
      purchase_type: holding.purchase_type,
      purchase_price: holding.purchase_price,
      quality_tag: holding.custom_fields?.category || 'Unknown',
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.put(`/holdings/${id}`, {
        ...editFormData,
        custom_fields: { category: editFormData.quality_tag }
      });
      toast.success('Holding updated');
      setEditingId(null);
      fetchHoldings();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to update holding');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleDelete = (id, symbol) => {
    showConfirm('delete', id, `Are you sure you want to delete the holding "${symbol}"?\n\nThis action will be recorded in history and can be rolled back.`);
  };

  const openPledgeDialog = async (holding) => {
    try {
      const lasRes = await api.get('/las-accounts');
      if (lasRes.data.length === 0) {
        toast.error('Please create a LAS account first');
        return;
      }
      setLASAccounts(lasRes.data);
      setPledgeData({
        holdingId: holding.id,
        symbol: holding.symbol,
        quantity: holding.quantity,
        lasAccountId: lasRes.data[0]?.id || '',
        pledgeQuantity: holding.quantity
      });
      setShowPledgeDialog(true);
    } catch (err) {
      toast.error('Failed to load LAS accounts');
    }
  };

  const handlePledge = async () => {
    try {
      await api.post(`/holdings/${pledgeData.holdingId}/pledge`, {
        las_account_id: pledgeData.lasAccountId,
        quantity: pledgeData.pledgeQuantity 
      });
      toast.success(`Successfully pledged ${pledgeData.pledgeQuantity} shares`);
      setShowPledgeDialog(false);
      fetchHoldings();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pledge shares');
    }
  };

  const openUnpledgeDialog = async (holding) => {
    setUnpledgeData({
      holdingId: holding.id,
      symbol: holding.symbol,
      quantity: holding.quantity,
      unpledgeQuantity: holding.quantity
    });
    setShowUnpledgeDialog(true);
  };

  const handleUnpledge = async () => {
    try {
      await api.post(`/holdings/${unpledgeData.holdingId}/unpledge`, {
        quantity: unpledgeData.unpledgeQuantity
      });
      toast.success(`Successfully unpledged ${unpledgeData.unpledgeQuantity} shares`);
      setShowUnpledgeDialog(false);
      fetchHoldings();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpledge shares');
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getFilteredAndSortedHoldings = () => {
    let filtered = [...holdings];
    
    if (holdingFilter === 'pledged') filtered = filtered.filter(h => h.pledged);
    else if (holdingFilter === 'free') filtered = filtered.filter(h => !h.pledged);
    
    if (selectedMember !== 'all') filtered = filtered.filter(h => h.member_id === selectedMember);
    if (selectedQualityTag !== 'all') {
      filtered = filtered.filter(h => (h.custom_fields?.category || 'Unknown') === selectedQualityTag);
    }
    
    return filtered.sort((a, b) => {
      let aVal, bVal;
      switch(sortConfig.key) {
        case 'symbol':
          return sortConfig.direction === 'asc' ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol);
        case 'quantity': aVal = a.quantity; bVal = b.quantity; break;
        case 'price': aVal = a.purchase_price; bVal = b.purchase_price; break;
        case 'value':
          aVal = (a.custom_fields?.current_price || a.purchase_price) * a.quantity;
          bVal = (b.custom_fields?.current_price || b.purchase_price) * b.quantity;
          break;
        case 'type':
          return sortConfig.direction === 'asc' ? (a.purchase_type || '').localeCompare(b.purchase_type || '') : (b.purchase_type || '').localeCompare(a.purchase_type || '');
        case 'tag':
          return sortConfig.direction === 'asc' ? (a.custom_fields?.category || '').localeCompare(b.custom_fields?.category || '') : (b.custom_fields?.category || '').localeCompare(a.custom_fields?.category || '');
        case 'status': aVal = a.pledged ? 1 : 0; bVal = b.pledged ? 1 : 0; break;
        case 'member':
          const aMember = members.find(m => m.id === a.member_id)?.name || '';
          const bMember = members.find(m => m.id === b.member_id)?.name || '';
          return sortConfig.direction === 'asc' ? aMember.localeCompare(bMember) : bMember.localeCompare(aMember);
        default: return 0;
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  // Company-wise aggregation
  const getCompanyWiseData = () => {
    let filtered = [...holdings];
    if (companyMemberFilter !== 'all') filtered = filtered.filter(h => h.member_id === companyMemberFilter);
    
    const companyMap = {};
    filtered.forEach(h => {
      const symbol = h.symbol;
      const price = h.custom_fields?.current_price || h.purchase_price;
      const value = price * h.quantity;
      
      if (!companyMap[symbol]) {
        companyMap[symbol] = { symbol, totalShares: 0, totalValue: 0, avgPrice: 0, holdings: [] };
      }
      companyMap[symbol].totalShares += h.quantity;
      companyMap[symbol].totalValue += value;
      companyMap[symbol].holdings.push({ ...h, memberName: members.find(m => m.id === h.member_id)?.name || 'Unknown' });
    });

    Object.values(companyMap).forEach(c => {
      c.avgPrice = c.totalValue / c.totalShares;
    });

    let companies = Object.values(companyMap);
    return companies.sort((a, b) => {
      let aVal, bVal;
      switch(companySortConfig.key) {
        case 'symbol':
          return companySortConfig.direction === 'asc' ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol);
        case 'shares': aVal = a.totalShares; bVal = b.totalShares; break;
        case 'value': aVal = a.totalValue; bVal = b.totalValue; break;
        default: return 0;
      }
      return companySortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const getMemberName = (memberId) => members.find(m => m.id === memberId)?.name || 'Unknown';
  const calculateHoldingValue = (h) => (h.custom_fields?.current_price || h.purchase_price) * h.quantity;

  const getQualityTagColor = (tag) => {
    const colors = { 'New': 'bg-green-100 text-green-800', 'High': 'bg-blue-100 text-blue-800', 'Moderate': 'bg-yellow-100 text-yellow-800', 'Low': 'bg-orange-100 text-orange-800', 'V Low': 'bg-red-100 text-red-800', 'VV Low': 'bg-red-200 text-red-900', 'Unknown': 'bg-gray-100 text-gray-800' };
    return colors[tag] || colors['Unknown'];
  };

  // Format large numbers compactly (e.g., 1.5 Cr, 50 L)
  const formatCompactValue = (value) => {
    if (!value) return '0';
    if (value >= 10000000) return `${(value / 10000000).toFixed(1)} Cr`;
    if (value >= 100000) return `${(value / 100000).toFixed(1)} L`;
    return Math.round(value).toLocaleString('en-IN');
  };

  // Get quality breakdown from summary (backend calculated)
  const getQualityBreakdown = () => {
    return summary?.quality_breakdown || [];
  };

  // Open holdings modal with pre-selected quality filter
  const openHoldingsWithQuality = async (qualityTag) => {
    await fetchHoldings();
    setSelectedQualityTag(qualityTag);
    setShowHoldingsModal(true);
  };

  const availableQualityTags = ['all', ...new Set(holdings.map(h => h.custom_fields?.category || 'Unknown'))];

  const renderSortHeader = (key, label) => (
    <th className="text-left py-3 px-3 font-semibold text-sm cursor-pointer hover:bg-gray-100" onClick={() => handleSort(key)}>
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === key ? <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span> : <ArrowUpDown size={12} className="text-gray-400" />}
      </div>
    </th>
  );

  if (loading) {
    return <Layout user={user} setUser={setUser}><div className="text-center py-20">Loading dashboard...</div></Layout>;
  }

  // Check for Zero State - no data in portfolio
  // Show Zero State if user has no holdings and no loans
  const hasPortfolioData = summary && (
    summary.total_portfolio_value > 0 || 
    summary.total_holdings > 0 || 
    (summary.total_las_loans && summary.total_las_loans > 0) ||
    (summary.total_mtf_loans && summary.total_mtf_loans > 0)
  );

  // For demo accounts: Show Zero State when toggle is ON, show data when toggle is OFF
  if (isDemoAccount && demoModeShowZeroState) {
    return (
      <>
        <ZeroStateDashboard user={user} setUser={setUser} onDataAdded={fetchSummary} />
        <DemoModeToggle 
          isDemoPremium={isDemoPremium} 
          demoModeShowZeroState={demoModeShowZeroState} 
          setDemoModeShowZeroState={setDemoModeShowZeroState} 
        />
      </>
    );
  }

  // For non-demo accounts: Show Zero State if no data
  if (!hasPortfolioData && !isDemoAccount) {
    return <ZeroStateDashboard user={user} setUser={setUser} onDataAdded={fetchSummary} />;
  }

  return (
    <Layout user={user} setUser={setUser}>
      {/* Demo Mode Toggle for demo accounts */}
      {isDemoAccount && (
        <DemoModeToggle 
          isDemoPremium={isDemoPremium} 
          demoModeShowZeroState={demoModeShowZeroState} 
          setDemoModeShowZeroState={setDemoModeShowZeroState} 
        />
      )}
      
      <div data-testid="dashboard-page">
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1 md:mb-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">Welcome, {user.name}</h1>
              {isDemoAccount && (
                <span className="px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full uppercase tracking-wider shadow-lg animate-pulse">
                  Demo
                </span>
              )}
            </div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">Your family office dashboard</p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <DataSourceHealthBadge latestPriceChange={latestPriceChange} onOpenHistory={() => setShowRefreshHistory(true)} />
            <AddTransactionDialog 
              onSuccess={fetchSummary} 
              buttonText="Add Transaction"
            />
            <button 
              onClick={handleRefreshPrices} 
              disabled={refreshing} 
              data-testid="refresh-prices-button" 
              className="p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50"
              title="Refresh Prices"
            >
              <RefreshCw size={20} className={`text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="col-span-1 lg:col-span-8 card" data-testid="portfolio-value-card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider mb-1 sm:mb-2">Total Portfolio Value</p>
                <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary mb-2 break-all">₹{Math.round(summary?.total_portfolio_value || 0).toLocaleString('en-IN')}</p>
                
                {/* Unlisted stocks value note */}
                {summary?.unlisted_stocks_count > 0 && (
                  <p className="text-xs text-gray-400 mb-2">
                    <span className="text-amber-600">*</span> Excludes {summary.unlisted_stocks_count} unlisted stocks (Cost: ₹{Math.round(summary.unlisted_stocks_cost || 0).toLocaleString('en-IN')})
                  </p>
                )}
                
                {/* Latest Price Change Indicator */}
                {latestPriceChange && latestPriceChange.has_data && (
                  <div className={`flex flex-wrap items-center gap-2 mb-2 ${latestPriceChange.value_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {latestPriceChange.value_change >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    <span className="text-sm font-medium">
                      {latestPriceChange.value_change >= 0 ? '+' : ''}₹{Math.round(Math.abs(latestPriceChange.value_change)).toLocaleString('en-IN')} 
                      <span className="ml-1">({latestPriceChange.change_percent >= 0 ? '+' : ''}{latestPriceChange.change_percent.toFixed(2)}%)</span>
                    </span>
                    <span className="text-xs text-gray-400">
                      since last refresh
                      {latestPriceChange.last_refresh && (
                        <span className="ml-1">
                          ({new Date(latestPriceChange.last_refresh).toLocaleString('en-IN', { 
                            day: '2-digit', 
                            month: 'short', 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true 
                          })})
                        </span>
                      )}
                    </span>
                    {dailySnapshot?.today_pnl && (
                      <span
                        className={`ml-2 text-xs px-2 py-0.5 rounded-full border ${
                          dailySnapshot.today_pnl.sells_count > 0
                            ? (dailySnapshot.today_pnl.realized_pnl >= 0
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                              : 'bg-rose-50 text-rose-700 border-rose-100')
                            : 'bg-gray-50 text-gray-500 border-gray-200'
                        }`}
                        title="Today's realized P&L from sell trades"
                        data-testid="today-pnl-pill"
                      >
                        {dailySnapshot.today_pnl.sells_count > 0
                          ? `Today P&L: ${dailySnapshot.today_pnl.realized_pnl >= 0 ? '+' : ''}₹${Math.round(Math.abs(dailySnapshot.today_pnl.realized_pnl)).toLocaleString('en-IN')} · ${dailySnapshot.today_pnl.sells_count} sell${dailySnapshot.today_pnl.sells_count > 1 ? 's' : ''}`
                          : 'No sells today'}
                      </span>
                    )}
                  </div>
                )}

                {/* Portfolio Swings: 1D / 1W / 1M (effective = adjusted for buys/sells) */}
                {portfolioSwings && (
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-3" data-testid="portfolio-swings">
                    {['1d', '1w', '1m'].map(key => {
                      const s = portfolioSwings.swings?.[key];
                      if (!s) return null;
                      const eff = s.effective_change ?? 0;
                      const isUp = eff >= 0;
                      const cls = !s.available ? 'bg-gray-50 text-gray-400 border-gray-200'
                        : isUp ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-red-50 text-red-700 border-red-200';
                      // Format YYYY-MM-DD → "24April2026"
                      const fmtDate = (iso) => {
                        if (!iso) return '';
                        const [y, m, d] = iso.split('-');
                        const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                        return `${parseInt(d, 10)}${months[parseInt(m, 10) - 1]}${y}`;
                      };
                      // Compact rupee: 12345678 → "1.23Cr", 123456 → "1.23L", 1234 → "1.2K"
                      const fmtCompact = (n) => {
                        const abs = Math.abs(n);
                        if (abs >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
                        if (abs >= 100000) return `${(n / 100000).toFixed(2)} L`;
                        if (abs >= 1000) return `${(n / 1000).toFixed(1)} K`;
                        return Math.round(n).toString();
                      };
                      const prevDateLabel = fmtDate(s.previous_date);
                      const targetLabel = fmtDate(s.target_date);
                      const isApprox = !!s.approx;
                      const tooltip = s.previous_date
                        ? `${isApprox ? 'Approx — no snapshot for ' + targetLabel + '; using ' + prevDateLabel + '. Run a Refresh to backfill.' : 'vs ' + prevDateLabel + ' (trading day)'}\nRaw: ₹${(s.value_change || 0).toLocaleString('en-IN')} (incl. capital flows)\nBuys: ₹${(s.buys_value || 0).toLocaleString('en-IN')}\nSells: ₹${(s.sells_value || 0).toLocaleString('en-IN')}\nEffective (market): ₹${eff.toLocaleString('en-IN')} (excl. capital flows)`
                        : 'No prior data';
                      return (
                        <div
                          key={key}
                          className={`p-2 sm:p-3 rounded-lg border min-w-0 ${cls}`}
                          data-testid={`swing-${key}`}
                          title={tooltip}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] sm:text-xs uppercase tracking-wider font-medium opacity-80">{s.label}</span>
                            {s.available && (isUp
                              ? <TrendingUp size={12} className="opacity-70" />
                              : <TrendingDown size={12} className="opacity-70" />)}
                          </div>
                          {s.available ? (
                            <>
                              {/* Compact Cr/L on mobile to prevent overflow; full number from sm: up */}
                              <div className="text-sm sm:text-base font-bold leading-tight truncate">
                                <span className="sm:hidden">{isUp ? '+' : '−'}₹{fmtCompact(Math.abs(eff))}</span>
                                <span className="hidden sm:inline">{isUp ? '+' : ''}₹{Math.round(Math.abs(eff)).toLocaleString('en-IN')}</span>
                              </div>
                              <div className="text-xs sm:text-sm font-medium">
                                {isUp ? '+' : ''}{(s.effective_pct ?? 0).toFixed(2)}%
                              </div>
                              <div className="text-[10px] mt-0.5 opacity-60 truncate" title={tooltip}>
                                vs {isApprox && targetLabel ? targetLabel : prevDateLabel}
                                {isApprox && <span className="ml-1 italic text-amber-700">(approx via {prevDateLabel})</span>}
                                {(s.net_capital || 0) !== 0 && (
                                  <span className="ml-1 italic">
                                    · adj {s.net_capital >= 0 ? '−' : '+'}₹{Math.round(Math.abs(s.net_capital) / 100000)}L
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="text-xs">No prior data</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <div className="flex flex-wrap gap-3 sm:gap-6 mt-3 sm:mt-4">
                  <div className="p-2 rounded"><p className="text-xs text-gray-500 uppercase mb-1">Pledged</p><p className="text-lg sm:text-xl font-semibold text-gray-700">₹{Math.round(summary?.total_pledged_value || 0).toLocaleString('en-IN')}</p></div>
                  <div className="p-2 rounded"><p className="text-xs text-gray-500 uppercase mb-1">Free</p><p className="text-lg sm:text-xl font-semibold text-gray-700">₹{Math.round(summary?.total_free_value || 0).toLocaleString('en-IN')}</p></div>
                </div>
              </div>
              <div className="p-3 sm:p-4 bg-[#D1FAE5] rounded-lg flex-shrink-0 ml-2"><Wallet className="text-primary" size={28} /></div>
            </div>
            
            {/* Quality Breakdown - Interactive */}
            <div className="border-t pt-4 mt-2">
              <p className="text-xs text-gray-500 uppercase mb-3">Holdings by Quality (Click to view)</p>
              <div className="flex flex-wrap gap-2 sm:gap-2.5">
                {getQualityBreakdown().map(({ tag, count, value }) => (
                  <button
                    key={tag}
                    onClick={() => count > 0 && navigate(`/companies?tab=holdings&quality=${encodeURIComponent(tag)}`)}
                    disabled={count === 0}
                    className={`px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-2xl transition-all flex flex-col items-start min-w-[88px] ${
                      count > 0
                        ? `hover:scale-105 hover:shadow-md ${getQualityTagColor(tag)}`
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid={`quality-pill-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="text-xs sm:text-sm font-semibold whitespace-nowrap leading-tight">{tag}: {count}</span>
                    {count > 0 && (
                      <span className="text-[11px] sm:text-xs opacity-80 mt-0.5 font-medium">
                        ₹{formatCompactValue(value)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            <p className="text-sm text-gray-500 italic mt-3 cursor-pointer hover:text-primary" onClick={() => navigate('/companies?tab=holdings')} data-testid="view-all-holdings-link">View all holdings →</p>
          </div>

          <div className="col-span-1 lg:col-span-4 lg:self-start grid grid-cols-1 gap-3 sm:gap-4">
            {/* Bank Savings mini card */}
            <div
              className="card cursor-pointer hover:shadow-xl transition-all"
              data-testid="bank-savings-mini-card"
              onClick={() => navigate('/banks')}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs sm:text-sm text-gray-500 truncate">Bank Savings</p>
                <Wallet size={16} className="text-blue-600" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-blue-700">
                ₹{Math.round(dailySnapshot?.banks?.savings_balance || 0).toLocaleString('en-IN')}
              </p>
              <p className="text-[10px] text-gray-500 mt-1">
                {dailySnapshot?.banks?.savings_count || 0} accounts
                {dailySnapshot?.banks?.loan_used > 0 && (
                  <span className="ml-1.5">· OD: ₹{Math.round((dailySnapshot.banks.loan_used) / 100000)}L used</span>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 md:mb-8">
          <StatCard
            icon={Shield}
            label="LAS Loans"
            value={summary?.total_las_loans}
            subtext={
              dailySnapshot?.ltv?.las
                ? `Pledged ₹${(dailySnapshot.ltv.las.pledged_value / 10000000).toFixed(2)} Cr · Free ₹${(Math.max(0, (dailySnapshot.ltv.las.eligible_amount || 0) - (dailySnapshot.ltv.las.pledged_value || 0)) / 10000000).toFixed(2)} Cr · ${(dailySnapshot.ltv.las.utilization_percent || 0).toFixed(1)}% util`
                : `${summary?.las_utilization || 0}% utilization`
            }
            testId="las-loans-card"
            onClick={() => navigate('/debt-management?tab=las')}
          />
          <StatCard
            icon={CreditCard}
            label="MTF Loans"
            value={summary?.total_mtf_loans}
            subtext={
              dailySnapshot?.ltv?.mtf
                ? `Pledged ₹${(dailySnapshot.ltv.mtf.total_funding_stock_value / 10000000).toFixed(2)} Cr · Free ₹${(Math.max(0, (dailySnapshot.ltv.mtf.total_funding_stock_value || 0) - (dailySnapshot.ltv.mtf.total_outstanding || 0)) / 10000000).toFixed(2)} Cr · ${(dailySnapshot.ltv.mtf.ltv_percent || 0).toFixed(1)}% LTV`
                : undefined
            }
            testId="mtf-loans-card"
            onClick={() => navigate('/debt-management?tab=mtf')}
          />
          <StatCard
            icon={TrendingUp}
            label="Total Debt"
            value={(summary?.total_las_loans || 0) + (summary?.total_mtf_loans || 0)}
            testId="total-debt-card"
            onClick={() => navigate('/debt-management?tab=dashboard')}
          />
        </div>

        {/* Daily Snapshot Tiles: Family Accounts / Today's P&L / Companies Held / Interest This Month */}
        {dailySnapshot && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 md:mb-8" data-testid="daily-snapshot-section">
            {/* Family Accounts */}
            <div
              className="card cursor-pointer hover:shadow-xl transition-all"
              onClick={fetchMembers}
              data-testid="family-accounts-tile"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Family Accounts</p>
                <Users size={16} className="text-emerald-600" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {summary?.total_members || 0}
              </p>
              <p className="text-[10px] text-gray-500 mt-1">Click to view members</p>
            </div>

            {/* Today's P&L */}
            <div
              className="card cursor-pointer hover:shadow-xl transition-all"
              onClick={() => navigate('/capital-gains')}
              data-testid="today-pnl-tile"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Today's P&L</p>
                {dailySnapshot.today_pnl.realized_pnl >= 0 ? <TrendingUp size={16} className="text-emerald-600" /> : <TrendingDown size={16} className="text-red-600" />}
              </div>
              <p className={`text-xl sm:text-2xl font-bold ${dailySnapshot.today_pnl.realized_pnl >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                {dailySnapshot.today_pnl.realized_pnl !== 0
                  ? `${dailySnapshot.today_pnl.realized_pnl >= 0 ? '+' : ''}₹${Math.abs(dailySnapshot.today_pnl.realized_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                  : '—'}
              </p>
              <p className="text-[10px] text-gray-500 mt-1">
                {dailySnapshot.today_pnl.sells_count > 0
                  ? `${dailySnapshot.today_pnl.sells_count} sells today`
                  : 'No sells today'}
              </p>
            </div>

            {/* Companies Held */}
            <div
              className="card cursor-pointer hover:shadow-xl transition-all"
              onClick={() => navigate('/companies')}
              data-testid="companies-held-tile"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Companies Held</p>
                <Building2 size={16} className="text-indigo-600" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {summary?.unique_companies || 0}
              </p>
              <div className="flex flex-wrap gap-1 mt-1.5 text-[10px]">
                <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">{summary?.listed_stocks_count || 0} Listed</span>
                <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded">{summary?.unlisted_stocks_count || 0} Unlisted</span>
                <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{summary?.mutual_funds_count || 0} MFs</span>
              </div>
            </div>

            {/* Interest Paid This Month */}
            <div
              className="card cursor-pointer hover:shadow-xl transition-all"
              onClick={() => navigate('/debt-management?tab=payments')}
              data-testid="interest-tile"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Interest & Principal ({dailySnapshot.interest.month_label})</p>
                <CreditCard size={16} className="text-amber-600" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-amber-700">
                {dailySnapshot.interest.this_month > 0
                  ? `₹${dailySnapshot.interest.this_month.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                  : '—'}
              </p>
              <p className="text-[10px] text-gray-500 mt-1 italic">
                {dailySnapshot.interest.this_month > 0
                  ? 'Click to view ledger →'
                  : 'Record monthly Bajaj payments →'}
              </p>
            </div>
          </div>
        )}

        {/* High-Priority Debt Insights */}
        {(summary?.las_utilization > 85 || summary?.mtf_safety_alerts > 0 || summary?.loan_over_utilized) && (
          <div className="mb-6 md:mb-8 space-y-3" data-testid="debt-insights-section">
            <div className="flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-500" />
                Loan Health Alerts
              </h3>
              <button 
                onClick={() => navigate('/debt-management')}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                View All <ArrowRight size={14} />
              </button>
            </div>
            
            {summary?.las_utilization > 85 && !dismissedAlerts['high_utilization'] && (
              <div className="p-3 sm:p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-amber-100 rounded-lg flex-shrink-0">
                    <Target size={18} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">High LAS Utilization ({summary.las_utilization}%)</p>
                    <p className="text-xs sm:text-sm text-amber-700 mt-0.5">Consider pledging more shares or reducing loan to maintain healthy margins.</p>
                  </div>
                </div>
                <button 
                  onClick={() => setDismissedAlerts(prev => ({ ...prev, high_utilization: true }))}
                  className="p-1 hover:bg-amber-200 rounded-full transition-colors flex-shrink-0"
                >
                  <X size={16} className="text-amber-600" />
                </button>
              </div>
            )}
            
            {summary?.mtf_safety_alerts > 0 && !dismissedAlerts['mtf_safety'] && (
              <div className="p-3 sm:p-4 rounded-lg bg-red-50 border border-red-200 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-red-100 rounded-lg flex-shrink-0">
                    <Shield size={18} className="text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-800 text-sm">MTF Safety Cover Warning</p>
                    <p className="text-xs sm:text-sm text-red-700 mt-0.5">{summary.mtf_safety_alerts} account(s) below safe threshold. Risk of margin call.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button 
                    onClick={() => navigate('/debt-management?tab=mtf')}
                    className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                  >
                    Review
                  </button>
                  <button 
                    onClick={() => setDismissedAlerts(prev => ({ ...prev, mtf_safety: true }))}
                    className="p-1 hover:bg-red-200 rounded-full transition-colors"
                  >
                    <X size={16} className="text-red-600" />
                  </button>
                </div>
              </div>
            )}
            
            {summary?.loan_over_utilized && !dismissedAlerts['loan_over'] && (
              <div className="p-3 sm:p-4 rounded-lg bg-red-50 border border-red-200 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-red-100 rounded-lg flex-shrink-0">
                    <CreditCard size={18} className="text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-800 text-sm">Loan Over-Utilization Alert</p>
                    <p className="text-xs sm:text-sm text-red-700 mt-0.5">One or more accounts exceeded their sanction limit. Immediate attention required.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button 
                    onClick={() => navigate('/debt-management?tab=las')}
                    className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                  >
                    Fix Now
                  </button>
                  <button 
                    onClick={() => setDismissedAlerts(prev => ({ ...prev, loan_over: true }))}
                    className="p-1 hover:bg-red-200 rounded-full transition-colors"
                  >
                    <X size={16} className="text-red-600" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Combined Market & Portfolio Performance Section */}
        <div className="card bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200" data-testid="market-performance-combined">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                <BarChart3 className="text-purple-600 flex-shrink-0" size={20} />
                <span>Market & Portfolio Performance</span>
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                <span className="block sm:inline">Today: <span className="font-medium text-gray-700">{new Date().toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</span></span>
                {latestChange?.latest_date && (
                  <span className="block sm:inline sm:ml-1"> • Data as of: <span className="font-medium text-purple-700">{new Date(latestChange.latest_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></span>
                )}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/performance')}
              className="flex items-center gap-1 text-purple-700 border-purple-300 hover:bg-purple-100 w-full sm:w-auto justify-center"
            >
              View Details <ArrowRight size={16} />
            </Button>
          </div>

          {/* Market Indices Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <p className="text-xs text-gray-500 uppercase mb-1">NSE Nifty <span className="text-xs font-normal">(Monthly)</span></p>
              <div className="flex items-center gap-2">
                <p className={`text-lg sm:text-xl font-bold ${(latestChange?.nifty_change_percent || summary?.nse_change_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(latestChange?.nifty_change_percent || summary?.nse_change_percent || 0) >= 0 ? '+' : ''}{(latestChange?.nifty_change_percent || summary?.nse_change_percent || 0).toFixed(2)}%
                </p>
                {(latestChange?.nifty_change_percent || summary?.nse_change_percent || 0) >= 0 ? <TrendingUp className="text-green-600" size={18} /> : <TrendingDown className="text-red-600" size={18} />}
              </div>
              <p className="text-sm text-gray-500">{latestChange?.nifty ? latestChange.nifty.toLocaleString('en-IN') : ''}</p>
            </div>
            
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <p className="text-xs text-gray-500 uppercase mb-1">BSE Sensex <span className="text-xs font-normal">(Monthly)</span></p>
              <div className="flex items-center gap-2">
                <p className={`text-lg sm:text-xl font-bold ${(latestChange?.sensex_change_percent || summary?.bse_change_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(latestChange?.sensex_change_percent || summary?.bse_change_percent || 0) >= 0 ? '+' : ''}{(latestChange?.sensex_change_percent || summary?.bse_change_percent || 0).toFixed(2)}%
                </p>
                {(latestChange?.sensex_change_percent || summary?.bse_change_percent || 0) >= 0 ? <TrendingUp className="text-green-600" size={18} /> : <TrendingDown className="text-red-600" size={18} />}
              </div>
              <p className="text-sm text-gray-500">{latestChange?.sensex ? latestChange.sensex.toLocaleString('en-IN') : ''}</p>
            </div>
            
            <div 
              className="p-3 bg-white rounded-lg shadow-sm cursor-pointer hover:bg-primary/5 border-2 border-transparent hover:border-primary/30 transition-all"
              onClick={handlePortfolioClick}
            >
              <p className="text-xs text-gray-500 uppercase mb-1">Your Portfolio <span className="text-xs font-normal text-primary">(vs Previous Month • Click for details)</span></p>
              <div className="flex items-center gap-2">
                <p className={`text-xl font-bold ${(latestChange?.change_percent || summary?.day_change_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(latestChange?.change_percent || summary?.day_change_percent || 0) >= 0 ? '+' : ''}{(latestChange?.change_percent || summary?.day_change_percent || 0).toFixed(2)}%
                </p>
                {(latestChange?.change_percent || summary?.day_change_percent || 0) >= 0 ? <TrendingUp className="text-green-600" size={18} /> : <TrendingDown className="text-red-600" size={18} />}
              </div>
              {latestChange?.change !== undefined && (
                <p className={`text-sm ${latestChange.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {latestChange.change >= 0 ? '+' : ''}₹{(latestChange.change / 10000000).toFixed(2)} Cr
                </p>
              )}
            </div>
          </div>

          {/* Performance Insights Row - Only show if data available */}
          {performanceSummary && performanceSummary.total_market_value > 0 && (
            <>
              <div className="border-t border-purple-200 pt-4 mt-2">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <p className="text-sm font-semibold text-purple-700 flex items-center gap-2">
                    <Zap size={16} /> Performance Insights
                    <span className="text-xs font-normal text-purple-500">· {performanceSummary.period_label || 'Overall'}</span>
                  </p>
                  <div className="inline-flex rounded-lg border border-purple-200 bg-white overflow-hidden text-xs" data-testid="perf-period-toggle">
                    {[
                      { k: 'overall', label: 'Overall' },
                      { k: 'yearly', label: 'This Year' },
                      { k: 'monthly', label: 'This Month' },
                    ].map(opt => (
                      <button
                        key={opt.k}
                        onClick={() => setPerfPeriod(opt.k)}
                        className={`px-3 py-1.5 transition-colors ${perfPeriod === opt.k
                          ? 'bg-purple-600 text-white font-semibold'
                          : 'text-purple-700 hover:bg-purple-50'}`}
                        data-testid={`perf-period-${opt.k}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div 
                    className="p-3 bg-white rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:border-green-300 border-2 border-transparent transition-all"
                    onClick={() => navigate('/performance')}
                  >
                    <p className="text-xs text-gray-500 uppercase mb-1">Total Gain <span className="text-xs text-primary">(Click)</span></p>
                    <p className={`text-xl font-bold ${performanceSummary.total_gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ₹{(performanceSummary.total_gain / 10000000).toFixed(2)} Cr
                    </p>
                    <p className={`text-sm ${performanceSummary.total_gain_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {performanceSummary.total_gain_percent >= 0 ? '+' : ''}{performanceSummary.total_gain_percent?.toFixed(1)}%
                    </p>
                  </div>
                  
                  <div 
                    className="p-3 bg-white rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:border-blue-300 border-2 border-transparent transition-all"
                    onClick={() => navigate('/dividends')}
                  >
                    <p className="text-xs text-gray-500 uppercase mb-1">Dividends Earned <span className="text-xs text-primary">(Click)</span></p>
                    <p className="text-xl font-bold text-blue-600">
                      ₹{(performanceSummary.total_dividends / 10000000).toFixed(2)} Cr
                    </p>
                    <p className="text-sm text-blue-600">
                      {performanceSummary.total_invested > 0 ? `${(performanceSummary.total_dividends / performanceSummary.total_invested * 100).toFixed(1)}% yield` : '0%'}
                    </p>
                  </div>
                  
                  <div 
                    className="p-3 bg-white rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:border-amber-300 border-2 border-transparent transition-all"
                    onClick={() => navigate('/debt-management')}
                  >
                    <p className="text-xs text-gray-500 uppercase mb-1">Interest Paid <span className="text-xs text-primary">(Click)</span></p>
                    <p className="text-xl font-bold text-amber-600">
                      ₹{(performanceSummary.total_interest_paid / 10000000).toFixed(2)} Cr
                    </p>
                    <p className="text-sm text-amber-600">Borrowing cost</p>
                  </div>
                  
                  <div 
                    className="p-3 bg-white rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:border-purple-300 border-2 border-transparent transition-all"
                    onClick={() => navigate('/performance')}
                  >
                    <p className="text-xs text-gray-500 uppercase mb-1">Net Return <span className="text-xs text-primary">(Click)</span></p>
                    <p className={`text-xl font-bold ${performanceSummary.net_return >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                      ₹{(performanceSummary.net_return / 10000000).toFixed(2)} Cr
                    </p>
                    <p className="text-sm text-purple-600">After all costs</p>
                  </div>
                </div>

                {/* Quick Insights */}
                <div className="flex flex-wrap gap-3">
                  {performanceSummary.total_gain_percent > 0 && (
                    <div 
                      className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full text-sm text-green-800 cursor-pointer hover:bg-green-200 transition-colors"
                      onClick={() => navigate('/performance')}
                    >
                      <TrendingUp size={14} /> Portfolio up {performanceSummary.total_gain_percent?.toFixed(1)}%
                    </div>
                  )}
                  {performanceSummary.total_dividends > 0 && (
                    <div 
                      className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full text-sm text-blue-800 cursor-pointer hover:bg-blue-200 transition-colors"
                      onClick={() => navigate('/dividends')}
                    >
                      <IndianRupee size={14} /> ₹{(performanceSummary.total_dividends / 10000000).toFixed(1)} Cr dividends
                    </div>
                  )}
                  {performanceSummary.investments_by_category?.length > 0 && (
                    <div 
                      className="flex items-center gap-2 px-3 py-1.5 bg-purple-100 rounded-full text-sm text-purple-800 cursor-pointer hover:bg-purple-200 transition-colors"
                      onClick={() => navigate('/companies')}
                    >
                      <Award size={14} /> Best: {performanceSummary.investments_by_category.reduce((best, curr) => curr.gain_percent > (best?.gain_percent || -Infinity) ? curr : best, null)?.category}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {summary?.las_utilization > 90 && !dismissedAlerts['las_utilization'] && (
          <div className="mt-8 card bg-[#FED7AA] border-[#EA580C]" data-testid="alert-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[#EA580C] text-white rounded-lg"><Shield size={24} /></div>
                <div><h3 className="font-bold text-lg mb-1">Action Needed: LAS Utilization High</h3><p className="text-gray-700">Your LAS loan utilization is at {summary.las_utilization}%. Consider pledging more shares or reducing loan amount.</p></div>
              </div>
              <button 
                onClick={() => setDismissedAlerts(prev => ({ ...prev, las_utilization: true }))}
                className="p-1.5 hover:bg-black/10 rounded-full transition-colors flex-shrink-0"
                title="Dismiss alert"
              >
                <X size={18} className="text-gray-600" />
              </button>
            </div>
          </div>
        )}


        {/* Company-wise Holdings Modal */}
        <Dialog open={showCompaniesModal} onOpenChange={setShowCompaniesModal}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-2xl flex items-center gap-2"><Building2 size={24} /> Company-wise Holdings</DialogTitle></DialogHeader>
            <div className="flex gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div><label className="text-sm font-medium mb-1 block">Filter by Member</label><select className="input-field" value={companyMemberFilter} onChange={(e) => setCompanyMemberFilter(e.target.value)}><option value="all">All Members</option>{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
              <div><label className="text-sm font-medium mb-1 block">Sort By</label><select className="input-field" value={companySortConfig.key} onChange={(e) => setCompanySortConfig(prev => ({ ...prev, key: e.target.value }))}><option value="symbol">Symbol</option><option value="shares">Total Shares</option><option value="value">Total Value</option></select></div>
              <div><label className="text-sm font-medium mb-1 block">Order</label><select className="input-field" value={companySortConfig.direction} onChange={(e) => setCompanySortConfig(prev => ({ ...prev, direction: e.target.value }))}><option value="asc">Ascending</option><option value="desc">Descending</option></select></div>
            </div>

            <div className="space-y-4">
              {getCompanyWiseData().map(company => (
                <div key={company.symbol} className="card border">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-primary">{company.symbol}</h3>
                      <p className="text-sm text-gray-500">{company.holdings.length} holding{company.holdings.length > 1 ? 's' : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">₹{company.totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                      <p className="text-sm text-gray-500">{company.totalShares.toLocaleString()} shares @ ₹{company.avgPrice.toFixed(2)} avg</p>
                    </div>
                  </div>
                  <div className="border-t pt-3">
                    <p className="text-sm font-medium mb-2">Breakdown by Account:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {company.holdings.map((h, idx) => (
                        <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
                          <p className="font-medium">{h.memberName}</p>
                          <p className="text-gray-600">{h.quantity.toLocaleString()} shares</p>
                          <p className="text-gray-500">₹{calculateHoldingValue(h).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-sm text-gray-600 font-medium">Total: {getCompanyWiseData().length} companies • {holdings.filter(h => companyMemberFilter === 'all' || h.member_id === companyMemberFilter).reduce((s, h) => s + h.quantity, 0).toLocaleString()} shares</div>
          </DialogContent>
        </Dialog>

        {/* History Modal */}
        <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><History size={20} /> Change History</DialogTitle></DialogHeader>
            {changeHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No changes recorded yet.</div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Review changes before rolling back. Rollback will restore the previous state.</p>
                {changeHistory.map((item) => {
                  const prev = item.previous_data || {};
                  const newData = item.new_data || {};
                  const isDelete = item.action === 'delete';
                  const isPledgeChange = item.description?.includes('Pledged') || item.description?.includes('Unpledged');
                  
                  return (
                    <div key={item.id} className={`p-4 rounded-lg border ${isDelete ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Header */}
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${isDelete ? 'bg-red-200 text-red-800' : 'bg-blue-200 text-blue-800'}`}>
                              {item.action.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-500">{new Date(item.created_at).toLocaleString('en-IN')}</span>
                          </div>
                          
                          {/* Holding Info */}
                          <div className="flex items-center gap-2 mb-3">
                            <span className="font-bold text-lg">{prev.symbol}</span>
                            <span className="text-gray-500">×</span>
                            <span className="font-medium">{prev.quantity?.toLocaleString()} shares</span>
                          </div>
                          
                          {/* Change Details */}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {/* Previous State */}
                            <div className="p-3 bg-white rounded border">
                              <p className="text-xs text-gray-500 uppercase mb-2 font-medium">Before Change</p>
                              <div className="space-y-1">
                                <p><span className="text-gray-500">Status:</span> <span className={`font-medium ${prev.pledged ? 'text-orange-600' : 'text-green-600'}`}>{prev.pledged ? 'Pledged' : 'Free'}</span></p>
                                <p><span className="text-gray-500">Price:</span> ₹{prev.purchase_price?.toFixed(2)}</p>
                                <p><span className="text-gray-500">Type:</span> {prev.purchase_type || 'N/A'}</p>
                                {prev.custom_fields?.category && <p><span className="text-gray-500">Quality:</span> {prev.custom_fields.category}</p>}
                              </div>
                            </div>
                            
                            {/* New State / What Changed */}
                            <div className="p-3 bg-white rounded border">
                              <p className="text-xs text-gray-500 uppercase mb-2 font-medium">{isDelete ? 'Action Taken' : 'After Change'}</p>
                              <div className="space-y-1">
                                {isDelete ? (
                                  <p className="text-red-600 font-medium">Record was deleted</p>
                                ) : isPledgeChange ? (
                                  <>
                                    <p><span className="text-gray-500">Status:</span> <span className={`font-medium ${newData.pledged ? 'text-orange-600' : 'text-green-600'}`}>{newData.pledged ? 'Pledged' : 'Free'}</span></p>
                                    {newData.las_account_id && <p><span className="text-gray-500">LAS Account:</span> Assigned</p>}
                                  </>
                                ) : (
                                  <>
                                    {newData.symbol && prev.symbol !== newData.symbol && <p><span className="text-gray-500">Symbol:</span> {newData.symbol}</p>}
                                    {newData.quantity && prev.quantity !== newData.quantity && <p><span className="text-gray-500">Quantity:</span> {newData.quantity}</p>}
                                    {newData.purchase_price && prev.purchase_price !== newData.purchase_price && <p><span className="text-gray-500">Price:</span> ₹{newData.purchase_price?.toFixed(2)}</p>}
                                    {newData.purchase_type && prev.purchase_type !== newData.purchase_type && <p><span className="text-gray-500">Type:</span> {newData.purchase_type}</p>}
                                    {newData.custom_fields?.category && <p><span className="text-gray-500">Quality:</span> {newData.custom_fields.category}</p>}
                                    {Object.keys(newData).length === 0 && <p className="text-gray-500 italic">Minor update</p>}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Rollback Info */}
                          <p className="text-xs text-gray-500 mt-3 italic">
                            Rollback will restore to: {prev.pledged ? 'Pledged' : 'Free'} status, ₹{prev.purchase_price?.toFixed(2)} price
                          </p>
                        </div>
                        
                        {/* Rollback Button */}
                        <Button 
                          onClick={() => handleRollback(item.id, item.description)} 
                          className="btn-outline flex items-center gap-1 text-sm whitespace-nowrap" 
                          size="sm"
                        >
                          <RotateCcw size={14} /> Rollback
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Members Modal */}
        <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
          <DialogContent className="max-w-4xl"><DialogHeader><DialogTitle className="text-2xl">Family Accounts</DialogTitle></DialogHeader><div className="grid grid-cols-1 md:grid-cols-2 gap-4">{members.map(m => (<div key={m.id} className="card"><h3 className="font-bold text-lg">{m.name}</h3><p className="text-gray-600">{m.relation}</p>{m.email && <p className="text-sm text-gray-500 mt-2">📧 {m.email}</p>}{m.phone && <p className="text-sm text-gray-500">📱 {m.phone}</p>}</div>))}</div></DialogContent>
        </Dialog>

        {/* LAS Accounts Modal */}
        <Dialog open={showLASModal} onOpenChange={setShowLASModal}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="text-2xl">LAS Accounts</DialogTitle></DialogHeader><div className="space-y-4">{lasAccounts.map(account => { const member = members.find(m => m.id === account.member_id); const pledgedHoldings = holdings.filter(h => h.las_account_id === account.id && h.pledged); const pledgedValue = pledgedHoldings.reduce((sum, h) => sum + calculateHoldingValue(h), 0); const maxLoan = pledgedValue * 0.5; const utilization = maxLoan > 0 ? (account.loan_amount / maxLoan) * 100 : 0; return (<div key={account.id} className="card"><div className="flex justify-between items-start mb-4"><div><h3 className="font-bold text-lg">{account.bank_name}</h3><p className="text-gray-600">{member?.name} - {account.account_number}</p></div><span className={`badge ${utilization > 90 ? 'badge-danger' : 'badge-success'}`}>{utilization.toFixed(1)}% utilized</span></div><div className="grid grid-cols-4 gap-4 mb-4"><div><p className="text-sm text-gray-500">Pledged Value</p><p className="font-bold">₹{pledgedValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p></div><div><p className="text-sm text-gray-500">Max Loan (50%)</p><p className="font-bold">₹{maxLoan.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p></div><div><p className="text-sm text-gray-500">Current Loan</p><p className="font-bold text-[#EA580C]">₹{account.loan_amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p></div><div><p className="text-sm text-gray-500">Pledged Stocks</p><p className="font-bold">{pledgedHoldings.length}</p></div></div>{pledgedHoldings.length > 0 && (<div className="border-t pt-3"><p className="text-sm font-medium mb-2">Pledged Holdings:</p><div className="flex flex-wrap gap-2">{pledgedHoldings.map(h => (<span key={h.id} className="badge badge-warning">{h.symbol} ({h.quantity})</span>))}</div></div>)}</div>); })}</div></DialogContent>
        </Dialog>

        {/* MTF Accounts Modal */}
        {/* Portfolio Performance Modal */}
        <Dialog open={showPortfolioModal} onOpenChange={setShowPortfolioModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center gap-2">
                <BarChart3 className="text-primary" size={28} />
                Portfolio Performance
              </DialogTitle>
            </DialogHeader>
            
            {/* Period Selector */}
            <div className="flex gap-2 mb-4">
              {['day', 'week', 'month', 'year', 'all'].map(period => (
                <button
                  key={period}
                  onClick={() => handlePeriodChange(period)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedPeriod === period 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
            
            {portfolioPerformance && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Period Change</p>
                    <p className={`text-xl font-bold ${portfolioPerformance.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {portfolioPerformance.change >= 0 ? '+' : ''}₹{(portfolioPerformance.change / 10000000).toFixed(2)} Cr
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Change %</p>
                    <p className={`text-xl font-bold ${portfolioPerformance.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {portfolioPerformance.change_percent >= 0 ? '+' : ''}{portfolioPerformance.change_percent.toFixed(2)}%
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Data Points</p>
                    <p className="text-xl font-bold text-gray-700">{portfolioPerformance.total_records}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Latest Value</p>
                    <p className="text-xl font-bold text-primary">
                      ₹{portfolioPerformance.snapshots?.length > 0 
                        ? (portfolioPerformance.snapshots[portfolioPerformance.snapshots.length - 1]?.portfolio_value / 10000000).toFixed(2) 
                        : '0'} Cr
                    </p>
                  </div>
                </div>
                
                {/* Performance vs Index Comparison */}
                {portfolioPerformance.index_data?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Portfolio vs Index Performance</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-2">Date</th>
                            <th className="text-right p-2">Portfolio Value</th>
                            <th className="text-right p-2">Portfolio %</th>
                            <th className="text-right p-2">Sensex</th>
                            <th className="text-right p-2">Sensex %</th>
                            <th className="text-right p-2">Nifty</th>
                            <th className="text-right p-2">Nifty %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {portfolioPerformance.index_data.slice(-20).map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-2">{item.date ? new Date(item.date).toLocaleDateString('en-IN') : '-'}</td>
                              <td className="p-2 text-right">₹{(item.portfolio_value / 10000000).toFixed(2)} Cr</td>
                              <td className={`p-2 text-right font-medium ${item.net_increase_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {item.net_increase_percent >= 0 ? '+' : ''}{item.net_increase_percent?.toFixed(2)}%
                              </td>
                              <td className="p-2 text-right">{item.sensex?.toLocaleString('en-IN')}</td>
                              <td className={`p-2 text-right ${item.sensex_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {item.sensex_change_percent >= 0 ? '+' : ''}{item.sensex_change_percent?.toFixed(2)}%
                              </td>
                              <td className="p-2 text-right">{item.nifty?.toLocaleString('en-IN')}</td>
                              <td className={`p-2 text-right ${item.nifty_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {item.nifty_change_percent >= 0 ? '+' : ''}{item.nifty_change_percent?.toFixed(2)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {portfolioPerformance.index_data.length > 20 && (
                      <p className="text-center text-gray-500 text-sm mt-2">
                        Showing last 20 of {portfolioPerformance.index_data.length} records
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showMTFModal} onOpenChange={setShowMTFModal}>
          <DialogContent className="max-w-4xl"><DialogHeader><DialogTitle className="text-2xl">MTF Accounts</DialogTitle></DialogHeader><div className="space-y-4">{mtfAccounts.map(account => { const member = members.find(m => m.id === account.member_id); const monthlyInterest = (account.total_loan * account.interest_rate) / 100 / 12; return (<div key={account.id} className="card"><div className="flex justify-between items-start mb-4"><div><h3 className="font-bold text-lg">{account.bank_name}</h3><p className="text-gray-600">{member?.name} - {account.account_number}</p></div></div><div className="grid grid-cols-3 gap-4"><div><p className="text-sm text-gray-500">Total Loan</p><p className="font-bold text-[#EA580C]">₹{account.total_loan.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p></div><div><p className="text-sm text-gray-500">Interest Rate</p><p className="font-bold">{account.interest_rate}% p.a.</p></div><div><p className="text-sm text-gray-500">Monthly Interest</p><p className="font-bold">₹{monthlyInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p></div></div></div>); })}</div></DialogContent>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={24} />
                Confirm Action
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-700 whitespace-pre-line">{confirmAction.message}</p>
            </div>
            <DialogFooter className="flex gap-3 justify-end">
              <Button 
                onClick={() => setShowConfirmDialog(false)} 
                className="btn-outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirm} 
                className={confirmAction.type === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-primary'}
              >
                {confirmAction.type === 'rollback' ? 'Yes, Rollback' : confirmAction.type === 'unpledge' ? 'Yes, Unpledge' : 'Yes, Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pledge Dialog */}
        <Dialog open={showPledgeDialog} onOpenChange={setShowPledgeDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="text-primary" size={24} />
                Pledge Shares
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Holding</p>
                <p className="font-bold text-lg">{pledgeData.symbol}</p>
                <p className="text-sm text-gray-600">Available: {pledgeData.quantity} shares</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Select LAS Account *</label>
                <select
                  className="input-field w-full"
                  value={pledgeData.lasAccountId}
                  onChange={(e) => setPledgeData({ ...pledgeData, lasAccountId: e.target.value })}
                >
                  {lasAccounts.map(acc => {
                    const member = members.find(m => m.id === acc.member_id);
                    return (
                      <option key={acc.id} value={acc.id}>
                        {acc.bank_name} - {member?.name || 'Unknown'} ({acc.account_number})
                      </option>
                    );
                  })}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Quantity to Pledge *</label>
                <input
                  type="number"
                  className="input-field w-full"
                  min="1"
                  max={pledgeData.quantity}
                  value={pledgeData.pledgeQuantity}
                  onChange={(e) => setPledgeData({ ...pledgeData, pledgeQuantity: Math.min(parseInt(e.target.value) || 0, pledgeData.quantity) })}
                />
                <p className="text-xs text-gray-500 mt-1">Max: {pledgeData.quantity} shares</p>
              </div>
            </div>
            <DialogFooter className="flex gap-3 justify-end">
              <Button 
                onClick={() => setShowPledgeDialog(false)} 
                className="btn-outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePledge}
                disabled={!pledgeData.lasAccountId || pledgeData.pledgeQuantity <= 0}
                className="btn-primary"
              >
                Pledge Shares
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unpledge Dialog */}
        <Dialog open={showUnpledgeDialog} onOpenChange={setShowUnpledgeDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldOff className="text-orange-600" size={24} />
                Unpledge Shares
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Holding</p>
                <p className="font-bold text-lg">{unpledgeData.symbol}</p>
                <p className="text-sm text-gray-600">Currently Pledged: {unpledgeData.quantity} shares</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Quantity to Unpledge *</label>
                <input
                  type="number"
                  className="input-field w-full"
                  min="1"
                  max={unpledgeData.quantity}
                  value={unpledgeData.unpledgeQuantity}
                  onChange={(e) => setUnpledgeData({ ...unpledgeData, unpledgeQuantity: Math.min(parseInt(e.target.value) || 0, unpledgeData.quantity) })}
                />
                <p className="text-xs text-gray-500 mt-1">Max: {unpledgeData.quantity} shares</p>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <p>Unpledged shares will be merged with any existing free shares of the same symbol.</p>
              </div>
            </div>
            <DialogFooter className="flex gap-3 justify-end">
              <Button 
                onClick={() => setShowUnpledgeDialog(false)} 
                className="btn-outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUnpledge}
                disabled={unpledgeData.unpledgeQuantity <= 0}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                Unpledge Shares
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RefreshHistoryModal
          open={showRefreshHistory}
          onClose={() => setShowRefreshHistory(false)}
        />
      </div>
    </Layout>
  );
};

export default Dashboard;
